import _ from 'lodash';
import React, {FC, useCallback, useMemo} from 'react';

import {isMultidimensionalArray, SyncScrollerProps} from '../../../../srcCommon/scroll/scrollerTypes';
import {AsyncMoreStatusesEnum} from '../../../store/appTypes';
import {VirtualScroller} from './virtualScroller';

// A Virtual scroller with a nicer API for the special case where all items are already loaded in memory.

/* eslint-disable react-hooks/exhaustive-deps */

type SyncVirtualScrollerProps<T> = SyncScrollerProps<T> & {
  className?: string;
};

const allLoadedStatuses = {
  BOTTOM: AsyncMoreStatusesEnum.ALL_LOADED,
  TOP: AsyncMoreStatusesEnum.ALL_LOADED,
};

const noop = () => undefined;
const getDefaultItemId = (item: any) => item.id as string | number;

export const SyncVirtualScroller = <T,>(props: SyncVirtualScrollerProps<T>): ReturnType<FC> => {
  const {items, itemHeight, getItemId = getDefaultItemId, renderItem, padding, className} = props;

  const itemIds: ReadonlyArray<string | number> | ReadonlyArray<ReadonlyArray<string | number>> = useMemo(
    () => (isMultidimensionalArray(items) ? items.map((item) => item.map(getItemId)) : items.map(getItemId)),
    [items],
  );

  const allItems = _.flatten(items);
  const itemById = useMemo(() => _(allItems).keyBy(getItemId).value(), [items]);

  const renderLoader = useCallback(() => null, []);

  const renderItemForId = useCallback(
    (id: string | number) => {
      const item = itemById[id];
      return item ? renderItem(item) : null;
    },
    [itemById, renderItem],
  );

  if (isMultidimensionalArray(itemIds)) {
    const {sectionHeaderHeight, renderSectionHeader} =
      'renderSectionHeader' in props ? props : {sectionHeaderHeight: 0, renderSectionHeader: noop};

    return (
      <VirtualScroller
        className={className}
        padding={padding}
        itemIds={itemIds}
        itemHeight={itemHeight}
        loaderHeight={0}
        moreStatuses={allLoadedStatuses}
        onLoadMore={noop}
        renderLoader={renderLoader}
        renderItem={renderItemForId}
        sectionHeaderHeight={sectionHeaderHeight}
        renderSectionHeader={renderSectionHeader}
      />
    );
  }

  return (
    <VirtualScroller
      className={className}
      padding={padding}
      itemIds={itemIds}
      itemHeight={itemHeight}
      loaderHeight={0}
      moreStatuses={allLoadedStatuses}
      onLoadMore={noop}
      renderLoader={renderLoader}
      renderItem={renderItemForId}
    />
  );
};
